'use client';

import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { CheckIcon } from '@heroicons/react/24/outline';
import { SetFormValues } from '@components/common/Forms';
import { SetMirrorData } from '@type/job-add';
import {
  getGuaranteedFirstDayApplicants,
  resolveJobAddV2BundleKey as resolveBundleKey,
} from '@components/templates/job-add-v2/utils/bundleType';

const BUNDLE_ORDER: Record<NonNullable<ReturnType<typeof resolveBundleKey>>, number> = {
  freemium: 0,
  agency: 1,
  hire: 2,
  pro: 3,
  plus: 4,
  standard: 5,
  live: 6,
};

export const isBundleSupportedInJobAddV2 = (bundle?: ApiSchema<'ActivationBundle'>): boolean => {
  if (!bundle) return false;
  const bundleKey = resolveBundleKey(bundle);
  if (!bundleKey) return false;
  return bundleKey !== 'live' && bundleKey !== 'agency';
};

const toPrice = (value?: number): number | undefined => {
  if (typeof value !== 'number' || Number.isNaN(value)) return undefined;
  return Math.ceil(value);
};

const getBundleCurrentPrice = (bundle?: ApiSchema<'ActivationBundle'>): number | undefined =>
  toPrice(bundle?.discountedCredits) ?? toPrice(bundle?.credits);

const getBundleOrder = (bundle?: ApiSchema<'ActivationBundle'>): number => {
  const bundleKey = resolveBundleKey(bundle);
  return bundleKey ? BUNDLE_ORDER[bundleKey] : 99;
};

export const sortSupportedBundlesForJobAddV2 = (bundles: ApiSchema<'ActivationBundle'>[]): ApiSchema<'ActivationBundle'>[] =>
  bundles
    .filter((bundle) => isBundleSupportedInJobAddV2(bundle))
    .sort((a, b) => {
      const priceDiff = (getBundleCurrentPrice(a) ?? Number.POSITIVE_INFINITY) - (getBundleCurrentPrice(b) ?? Number.POSITIVE_INFINITY);
      if (priceDiff !== 0) return priceDiff;

      return getBundleOrder(a) - getBundleOrder(b);
    });

export const getDisplayBundlesForJobAddV2 = (bundles: ApiSchema<'ActivationBundle'>[]): ApiSchema<'ActivationBundle'>[] =>
  sortSupportedBundlesForJobAddV2(bundles);

type BundleTitleTranslator = (key: string, options?: Record<string, unknown>) => string;

export const getBundleDisplayTitleV2 = (bundle: ApiSchema<'ActivationBundle'> | undefined, t: BundleTitleTranslator): string => {
  const bundleKey = resolveBundleKey(bundle);

  if (bundleKey === 'live') return bundle?.title || t('job.bundle.name.live') || 'Live';
  if (bundleKey === 'plus' || bundleKey === 'pro') return bundle?.title || t('job.add.extra.promo.agent');

  return bundle?.title || t('job.add.extra.promo.agent');
};

const getBundleShortTitleV2 = (bundle: ApiSchema<'ActivationBundle'> | undefined, t: BundleTitleTranslator): string => {
  const bundleKey = resolveBundleKey(bundle);
  if (bundleKey === 'freemium') return t('standalone.freemium.bundle.name');

  const displayTitle = getBundleDisplayTitleV2(bundle, t);
  const parenthesizedTitle = displayTitle.match(/\(([^)]+)\)/)?.[1]?.trim();
  if (parenthesizedTitle) return parenthesizedTitle;

  if (bundleKey === 'hire') return 'Hire';
  if (bundleKey === 'pro') return 'Pro';
  if (bundleKey === 'plus') return 'Plus';
  if (bundleKey === 'standard') return 'Start';

  return displayTitle;
};

const getBundlePriceData = (
  bundle?: ApiSchema<'ActivationBundle'>,
): {
  oldPrice?: number;
  currentPrice?: number;
} => {
  const oldPrice = toPrice(bundle?.credits);
  const discountedPrice = toPrice(bundle?.discountedCredits);
  const currentPrice = discountedPrice ?? oldPrice;

  if (typeof currentPrice !== 'number') {
    return {};
  }

  if (typeof oldPrice === 'number' && oldPrice > currentPrice) {
    return { oldPrice, currentPrice };
  }

  return { currentPrice };
};

export const getBundleCtaTextV2 = (bundle: ApiSchema<'ActivationBundle'> | undefined, t: BundleTitleTranslator): string => {
  const title = getBundleShortTitleV2(bundle, t);
  const { currentPrice } = getBundlePriceData(bundle);

  if (typeof currentPrice === 'number') {
    return `${title} - ${currentPrice} ${t('global.label.euro')}`;
  }

  return title;
};

interface ActivationBundleCardV2Props {
  bundle: ApiSchema<'ActivationBundle'>;
  isSelected: boolean;
  disabled?: boolean;
  onSelect: (bundle: ApiSchema<'ActivationBundle'>) => void;
  t: BundleTitleTranslator;
}

const getBundleSubtitleV2 = (bundle: ApiSchema<'ActivationBundle'> | undefined, t: BundleTitleTranslator): string => {
  const bundleKey = resolveBundleKey(bundle);
  if (bundleKey === 'freemium') return t('mc.services.bundle.freemium.mainbenefit');
  if (bundleKey === 'agency') return t('mc.services.bundle.agency.subtitle');
  if (bundleKey === 'hire') return t('mc.services.bundle.hire.mainbenefit');
  if (bundleKey === 'pro') return t('mc.services.bundle.pro.mainbenefit');
  if (bundleKey === 'plus') return t('mc.services.bundle.plus.mainbenefit');
  if (bundleKey === 'standard') return t('mc.services.bundle.start_view.subtitle');

  return bundle?.subtitle || '';
};

/**
 * Optional second bullet rendered under the subtitle. Only Plus / Pro / Hire
 * cards have a second point today; Start / other bundles return '' and the
 * card renders one bullet.
 */
const getBundleBullet2V2 = (bundle: ApiSchema<'ActivationBundle'> | undefined, t: BundleTitleTranslator): string => {
  const bundleKey = resolveBundleKey(bundle);
  if (bundleKey === 'standard') return t('mc.services.bundle.standard.subtitle.bullet2');
  if (bundleKey === 'hire') return t('mc.services.bundle.hire.subtitle.bullet2');
  if (bundleKey === 'pro') return t('mc.services.bundle.pro.subtitle.bullet2');
  if (bundleKey === 'plus') return t('mc.services.bundle.plus.subtitle.bullet2');
  if (bundleKey === 'freemium') return t('mc.services.bundle.freemium.benefit');

  return '';
};

/**
 * First bullet: guaranteed applicants in the first day. Only Start / Plus / Pro
 * carry a guarantee; other bundles return '' and the bullet is not rendered.
 */
const getBundleGuaranteedApplicantsV2 = (bundle: ApiSchema<'ActivationBundle'> | undefined, t: BundleTitleTranslator): string => {
  const applicants = getGuaranteedFirstDayApplicants(resolveBundleKey(bundle));
  if (!applicants) return '';

  return t('mc.services.bundle.guaranteed_first_day_applicants', { applicants });
};

const getBundlePriceSubtitleV2 =(bundle: ApiSchema<'ActivationBundle'> | undefined, t: BundleTitleTranslator): string => {
  const bundleKey = resolveBundleKey(bundle);
  if (bundleKey === 'freemium') return t('price_per_interview_freemium');
  if (bundleKey === 'standard') return t('price_per_interview_start');
  if (bundleKey === 'plus') return t('price_per_interview_plus');
  if (bundleKey === 'pro') return t('price_per_interview_pro');

  return '';
};

const ActivationBundleCardV2 = (props: ActivationBundleCardV2Props) => {
  const {
    bundle, isSelected, disabled = false, onSelect, t,
  } = props;
  const { oldPrice, currentPrice } = getBundlePriceData(bundle);
  const priceSubtitle = getBundlePriceSubtitleV2(bundle, t);
  const subtitle = getBundleSubtitleV2(bundle, t);
  const bullet2 = getBundleBullet2V2(bundle, t);
  const guaranteedApplicants = getBundleGuaranteedApplicantsV2(bundle, t);
  const isFreeBundle = currentPrice === 0;
  // Independent of the price wording: a bundle discounted down to zero — by a
  // subscription that covers it, say — still needs to show what it would have
  // cost, otherwise "gratuit" alone gives no sense of the benefit. The freemium
  // bundle has no higher list price, so nothing is struck through there.
  const hasListPrice = typeof oldPrice === 'number' && oldPrice > (currentPrice ?? oldPrice);

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onSelect(bundle)}
      className={`flex h-full min-h-48 w-full flex-col rounded-2xl border bg-surface p-5 text-left transition-colors ${isSelected ? 'border-secondary bg-secondary/5 ring-2 ring-secondary/20' : 'border-input/70 hover:border-secondary/50'} ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
    >
      <div className="text-xl font-bold leading-tight text-ink">{getBundleShortTitleV2(bundle, t)}</div>

      {priceSubtitle && <div className="mt-1 text-sm font-normal text-ink-medium">{priceSubtitle}</div>}

      {guaranteedApplicants && (
        <div className="mt-4 flex items-start gap-2 text-sm text-ink-medium">
          <CheckIcon className="mt-0.5 size-4 shrink-0 stroke-2 text-secondary" />
          <span>{guaranteedApplicants}</span>
        </div>
      )}

      <div className={`${guaranteedApplicants ? 'mt-2' : 'mt-4'} flex items-start gap-2 text-sm text-ink-medium`}>
        <CheckIcon className="mt-0.5 size-4 shrink-0 stroke-2 text-secondary" />
        <span>{subtitle}</span>
      </div>

      {bullet2 && (
        <div className="mt-2 flex items-start gap-2 text-sm text-ink-medium">
          <CheckIcon className="mt-0.5 size-4 shrink-0 stroke-2 text-secondary" />
          <span>{bullet2}</span>
        </div>
      )}

      <div className="mt-auto flex flex-wrap items-baseline justify-end gap-x-2 gap-y-1 pt-4 text-lg font-semibold text-secondary">
        {hasListPrice && (
          <span className="text-sm font-normal text-ink-medium line-through">
            {oldPrice} {t('employer.branding.price.desc')}
          </span>
        )}

        {isFreeBundle ? (
          <span>{t('mc.services.bundle.freemium.price')}</span>
        ) : (
          <span>
            {typeof currentPrice === 'number' ? currentPrice : 0} {t('employer.branding.price.desc')}
          </span>
        )}
      </div>
    </button>
  );
};

interface JobBundleSelectionSectionV2Props {
  setFormValues: SetFormValues;
  setMirrorData?: SetMirrorData;
  bundles?: ApiSchema<'ActivationBundle'>[];
  selectedBundleId?: string;
  inActivePeriod?: boolean;
  /** Highlights the section after an activation attempt with nothing picked. */
  hasMissingSelectionError?: boolean;
}

export const JobBundleSelectionSectionV2 = (props: JobBundleSelectionSectionV2Props) => {
  const {
    setFormValues, setMirrorData, bundles = [], selectedBundleId, inActivePeriod, hasMissingSelectionError = false,
  } = props;

  const { t } = useTranslation('common');

  const availableBundles = useMemo(
    () => sortSupportedBundlesForJobAddV2(bundles).filter((bundle) => bundle.available),
    [bundles],
  );

  // Freemium is no longer offered as a card — it is what the client gets by
  // deferring the choice, via the checkbox below. It stays in the bundle list
  // though: the checkbox still submits its packageId, which is what makes the
  // backend activate the job as freemium.
  const freemiumBundle = useMemo(
    () => availableBundles.find((bundle) => resolveBundleKey(bundle) === 'freemium'),
    [availableBundles],
  );
  const paidBundles = useMemo(
    () => availableBundles.filter((bundle) => resolveBundleKey(bundle) !== 'freemium'),
    [availableBundles],
  );

  // Derived, not stored: a second source of truth would drift from the form's
  // packageId. Note this is strictly "freemium is selected" — an empty form is
  // *no* choice, not a deferred one, so the box starts unchecked and the client
  // has to say which of the two they mean.
  const isChoiceDeferred = Boolean(freemiumBundle) && selectedBundleId === freemiumBundle?.packageId;

  const selectBundle = (bundle?: ApiSchema<'ActivationBundle'>) => {
    if (!bundle?.available || inActivePeriod) return;

    setFormValues((prev) => ({ ...(prev as Record<string, unknown>), packageId: bundle.packageId }));
    setMirrorData?.({ packageId: bundle.packageId, bundle: bundle.identifier });
  };

  // Unchecking "I will choose later" puts the form back to no choice at all,
  // which the activation guard rejects — deliberately. The client has to make
  // one of the two decisions rather than fall into freemium by inaction.
  const clearSelection = () => {
    if (inActivePeriod) return;

    setFormValues((prev) => ({ ...(prev as Record<string, unknown>), packageId: undefined }));
    setMirrorData?.({ packageId: undefined, bundle: undefined });
  };


  return (
    <div className="grid gap-5">
      <span className="mb-1 inline-block px-0 text-sm font-medium text-ink">{t('job.add.activate.unlimited.text')}</span>

      <div className="grid gap-5">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {paidBundles.map((bundle) => (
            <ActivationBundleCardV2
              key={bundle.packageId}
              bundle={bundle}
              isSelected={selectedBundleId === bundle.packageId}
              disabled={inActivePeriod}
              onSelect={(nextBundle) => selectBundle(nextBundle)}
              t={t}
            />
          ))}
        </div>

        {/* Hidden when the offered set has no freemium bundle: there would be
            nothing for the checkbox to submit, and the paid cards are then the
            only way forward anyway. */}
        {freemiumBundle && (
          <label
            htmlFor="job-add-defer-bundle-choice"
            className={`inline-flex items-center gap-2 text-sm ${
              inActivePeriod ? 'cursor-default text-ink-medium' : 'cursor-pointer text-ink-medium'
            }`}
          >
            <input
              id="job-add-defer-bundle-choice"
              type="checkbox"
              className="size-4 shrink-0 rounded border-input/70 text-secondary accent-secondary focus-visible:ring-[3px] focus-visible:ring-secondary/20 disabled:opacity-60"
              checked={isChoiceDeferred}
              disabled={inActivePeriod}
              // Both directions are real choices: ticking it selects freemium,
              // clearing it returns the form to "nothing picked", which blocks
              // activation until the client decides.
              onChange={() => (isChoiceDeferred ? clearSelection() : selectBundle(freemiumBundle))}
              aria-describedby={hasMissingSelectionError ? 'job-add-bundle-error' : undefined}
            />
            <span>{t('job.add.bundle.choose-later')}</span>
          </label>
        )}

        {/* Shown only after an activation attempt with nothing picked — the
            section is not pre-marked as invalid on a form the client has not
            submitted yet. */}
        {hasMissingSelectionError && (
          <p id="job-add-bundle-error" role="alert" className="text-sm font-medium text-error">
            {t('job.add.bundle.required.error')}
          </p>
        )}
      </div>
    </div>
  );
};
